import * as XLSX from 'xlsx';
// xlsx-js-style is a SheetJS fork with an identical API that preserves cell
// styles on write. The base `xlsx` build silently drops them.
import * as XLSXStyle from 'xlsx-js-style';

// Mirrors the backend header styling in generated-file-download.ts
const XLSX_HEADER_FILL = 'FFD9E1F2'; // light blue — standard header bar
const XLSX_HEADER_FONT_COLOR = 'FF1F3864'; // dark blue header text

function extractCellText(cell: HTMLTableCellElement): string {
  let out = '';

  const walk = (node: Node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      out += node.textContent ?? '';
      return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return;

    const el = node as HTMLElement;
    const refIndex = el.getAttribute('data-kim-ref');
    if (refIndex !== null) {
      out += `[${refIndex}]`;
      return;
    }
    el.childNodes.forEach(walk);
  };

  cell.childNodes.forEach(walk);
  return out.replace(/\s+/g, ' ').trim();
}

function extractRows(table: HTMLTableElement): string[][] {
  const rows: string[][] = [];

  const pushRow = (htmlRow: HTMLTableRowElement) => {
    const cells = Array.from(htmlRow.cells).map(extractCellText);
    rows.push(cells);
  };

  if (table.tHead) {
    Array.from(table.tHead.rows).forEach(pushRow);
  }
  Array.from(table.tBodies).forEach((body) => {
    Array.from(body.rows).forEach(pushRow);
  });

  return rows;
}

export function exportKimTableToXlsx(table: HTMLTableElement, filename = 'kim-table.xlsx'): void {
  const rows = extractRows(table);
  const worksheet = XLSX.utils.aoa_to_sheet(rows);

  const headerRow = rows[0] ?? [];
  // Kim tables hold long-form prose in every column, so wrap them all.
  worksheet['!cols'] = headerRow.map(() => ({ wch: 60 }));

  // Bold, shaded header bar with wrapping.
  for (let colIdx = 0; colIdx < headerRow.length; colIdx++) {
    const cell = worksheet[XLSX.utils.encode_cell({ r: 0, c: colIdx })] as XLSX.CellObject | undefined;
    if (!cell) continue;
    cell.s = {
      font: { bold: true, color: { rgb: XLSX_HEADER_FONT_COLOR } },
      fill: { fgColor: { rgb: XLSX_HEADER_FILL } },
      alignment: { vertical: 'center', wrapText: true },
    };
  }

  // Per-column filter dropdowns across the full table.
  if (rows.length > 0 && headerRow.length > 0) {
    worksheet['!autofilter'] = {
      ref: XLSX.utils.encode_range({
        s: { r: 0, c: 0 },
        e: { r: rows.length - 1, c: headerRow.length - 1 },
      }),
    };
  }

  // Wrap text on every data cell.
  for (let rowIdx = 1; rowIdx < rows.length; rowIdx++) {
    for (let colIdx = 0; colIdx < headerRow.length; colIdx++) {
      const cell = worksheet[XLSX.utils.encode_cell({ r: rowIdx, c: colIdx })] as XLSX.CellObject | undefined;
      if (cell) cell.s = { alignment: { wrapText: true, vertical: 'top' } };
    }
  }

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Table');
  // Write via xlsx-js-style so the cell styles survive.
  XLSXStyle.writeFile(workbook, filename);
}
